import { ICollectionTag } from '@monofront/types';
// import CarouselTags from '@components/CarouselTags';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { useUserTags, useMediaQuery } from '@monofront/business/hooks';
import { Description } from './Description';
import Link from 'next/link';
import { useCacheFirstAuthentication } from '@context/persistedAuthContext';
import {
  COLLECTIONS_TAGS,
  FRESH_USER,
  ULTRA_FRESH_USER,
  AB_TEST_FEATURE_FLAG_MULTILINE_CHIPS,
} from '@monofront/business/constants';
import CarouselTags from '@components/CarouselTags';
import { useFeatureIsOn } from '@monofront/business/contexts';

interface IHeroCollectionProps {
  title: string;
  image?: string;
  descriptionHtml: string;
  childCollections?: ICollectionTag[];
}

const defaultCollectionVisualPath = '/images/default-collection-visual.jpg';

export const HeroCollection: React.FC<IHeroCollectionProps> = ({
  title,
  image = defaultCollectionVisualPath,
  descriptionHtml,
  childCollections,
}) => {
  const isMobileClient = useMediaQuery(800);
  const localTags = useUserTags(useCacheFirstAuthentication);
  const isMultilineChipABtestEnabled = useFeatureIsOn(
    AB_TEST_FEATURE_FLAG_MULTILINE_CHIPS,
  );

  return (
    <div
      className="tw-hidden tw-rounded-2xl tw-bg-beige-200 desktop:tw-flex desktop:tw-min-h-[120px]
"
    >
      {image ? (
        <div className="tw-relative tw-hidden tw-w-[250px] tw-shrink-0 desktop:tw-block [&>span]:tw-min-h-full [&_img]:tw-rounded-[16px_0_0_16px]">
          <ResponsiveImage
            src={image}
            alt={title}
            layout="fill"
            objectFit="cover"
            priority
            sizes="(max-width: 800px) 250px, (max-width: 1300px) 500px, 250px"
          />
        </div>
      ) : null}
      <div className="tw-w-[calc(100%_-_250px)] tw-grow tw-px-[3.2vw] tw-py-4 desktop:tw-my-auto desktop:tw-px-6 ">
        <h1 className="tw-lf-title-md tw-mb-2 !tw-leading-[36.4px] desktop:tw-lf-title-xl">
          {title}
        </h1>
        <Description
          description={descriptionHtml}
          UNSAFE_className="[&_p]:tw-mb-[1.4rem]"
          enableSeeMore
        />
        {!isMobileClient &&
          childCollections &&
          (isMultilineChipABtestEnabled ? (
            <div className="tw-my-4 tw-flex tw-flex-wrap tw-gap-2">
              {childCollections
                ?.filter((collection) => {
                  const isFreshUser = localTags.includes(FRESH_USER);
                  const isUltraFreshUser = localTags.includes(ULTRA_FRESH_USER);

                  const isFreshCollection = collection._tags?.includes(
                    COLLECTIONS_TAGS.FRESH,
                  );
                  const isUltraFreshCollection = collection._tags?.includes(
                    COLLECTIONS_TAGS.ULTRA_FRESH,
                  );

                  return (
                    (!isFreshCollection || isFreshUser) &&
                    (!isUltraFreshCollection || isUltraFreshUser)
                  );
                })
                .map((collection) => (
                  <Link
                    className="tw-lf-chip tw-lf-chip-suggestion tw-lf-chip-button"
                    key={collection.objectID ?? collection.handle}
                    href={`/collections/${collection.handle}`}
                    prefetch={false}
                  >
                    {collection.title}
                  </Link>
                ))}
            </div>
          ) : (
            <CarouselTags tagList={childCollections} />
          ))}
      </div>
    </div>
  );
};
